import {
  call,
  put, takeEvery,
} from 'redux-saga/effects';
import { DEBUG_SEND_GEOMETRY_INSTEAD_OF_BOUNDARY_ID } from '~/_shared/constants/debugConstants';
import { select } from '~/_shared/utils/saga/effects';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type PickAction } from '~/_shared/utils/types/action.type';
import { AppErrorType } from '~/appError/appErrorType.enum';
import {
  type BulkBoundaryCombineGeometryCalculationRequest,
  bulkUpdateBoundaryGeometry,
  getBulkBoundaryCombineGeometry,
  type UpdateBoundaryRequestModel,
} from '~/store/boundaries/boundaries.repository';
import { boundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryItemsUpdateItems } from '~/store/boundaryItems/boundaryItems.actionCreators';
import { createBoundaryStateItemFromBoundaryResponse } from '~/store/boundaryItems/boundaryItems.factory';
import { mapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { type BoundaryTerritoryGroupsAction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import { BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import type { BoundarySelectAction } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.action';
import {
  buildGeometryCalculations, calculateSubtractions, convertBoundaryCalculationsToGeometryCalculations,
  prepareAdditions,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.utils';
import { MAP_SETTINGS_BOUNDARIES_SET_PRIMARY_BOUNDARY_GROUP_ID } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionTypes';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { translate } from '~/translations/Trans';
import {
  boundarySelectEditDeactivate, boundarySelectQuickEditSubmitted,
} from './boundarySelect.actionCreators';
import { boundarySelectSelector } from './boundarySelect.selectors';
import {
  type BoundarySelectState,
  isBoundarySelectEditBoundaryTerritory,
  isBoundarySelectEditCustomBoundary,
} from './boundarySelect.state';

export function* boundarySelectSagas() {
  yield takeEvery(MAP_SETTINGS_BOUNDARIES_SET_PRIMARY_BOUNDARY_GROUP_ID, deactivateBoundarySelect);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED, onBoundaryTerritoryGroupRemove);
  yield takeEvery(boundarySelectQuickEditSubmitted, onBoundarySelectQuickEditSubmitted);
}

function* onBoundaryTerritoryGroupRemove(action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED>) {
  const boundarySelectState: BoundarySelectState = yield select<BoundarySelectState>(boundarySelectSelector);
  const { boundaryTerritoryGroups: removedBTGs } = action.payload;

  if (isBoundarySelectEditBoundaryTerritory(boundarySelectState) || isBoundarySelectEditCustomBoundary(boundarySelectState)) {
    if (removedBTGs.some(btg => btg.boundaryTerritoryGroupId === boundarySelectState.selectionModeData?.boundaryTerritoryGroupId)) {
      yield deactivateBoundarySelect();
    }
  }
}

function* deactivateBoundarySelect() {
  yield put(boundarySelectEditDeactivate());
}

function* onBoundarySelectQuickEditSubmitted(action: PickAction<BoundarySelectAction, typeof boundarySelectQuickEditSubmitted.type>) {
  const { payload: { selectState, baseBoundaryGroupId, onDone } } = action;
  try {
    const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
    const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
    const boundaryGroups: ReturnType<typeof boundaryGroupsSelector> = yield select(boundaryGroupsSelector);
    const boundariesMap: ReturnType<typeof mapBoundariesSelector> = yield select(mapBoundariesSelector);
    const boundaries = boundariesMap.get(baseBoundaryGroupId);
    const boundaryList = boundaries ? [...boundaries.values()] : [];
    const boundaryGroup = boundaryGroups.find(boundaryGroup => boundaryGroup.id === baseBoundaryGroupId);

    if (!boundaryList || !boundaryGroup || !clientId || !mapId) {
      return;
    }

    const removals = calculateSubtractions(selectState);
    const additions = prepareAdditions({
      boundaryGroupId: baseBoundaryGroupId,
      boundaryIds: boundaryList.map(({ id }) => id),
    },
    selectState);

    const combineRemovedBoundariesRequest: BulkBoundaryCombineGeometryCalculationRequest = {
      base: removals,
      include: [],
      subtract: [],
    };

    const calculations = {
      ...buildGeometryCalculations(
        {
          boundaryGroupId: baseBoundaryGroupId,
          boundaryIds: boundaryList.map(({ id }) => id),
        },
        { type: 'query', query_id: 'removals' },
        additions,
      ),
      removals: combineRemovedBoundariesRequest,
    };

    const geometryResponse: Awaited<ReturnType<typeof getBulkBoundaryCombineGeometry>> = yield call(
      getBulkBoundaryCombineGeometry,
      clientId,
      DEBUG_SEND_GEOMETRY_INSTEAD_OF_BOUNDARY_ID ? convertBoundaryCalculationsToGeometryCalculations(calculations, boundariesMap) : calculations,
    );

    const boundaryUpdateRequests = Object.entries(geometryResponse.results)
      .map(([stringBoundaryId, geometryResult]) => {
        const boundaryId = Number(stringBoundaryId);
        const boundary = boundaryList.find(({ id }) => id === boundaryId);

        if (!boundary || !geometryResult.geometry) {
          return null;
        }

        return {
          boundary_id: boundaryId,
          geometry: geometryResult.geometry,
          map_id: mapId,
        } satisfies UpdateBoundaryRequestModel;
      }).filter(notNullsy);

    const updatedBoundaries: Awaited<ReturnType<typeof bulkUpdateBoundaryGeometry>> =
      yield call(bulkUpdateBoundaryGeometry, clientId, boundaryUpdateRequests);

    const updatedBoundaryStateItemsWithETags = updatedBoundaries.map(boundary => createBoundaryStateItemFromBoundaryResponse(boundary, { enforceLabelBoundsRecalc: true }));
    const latestEtag = updatedBoundaryStateItemsWithETags.reduce((latest, { etag }) => {
      if (etag && (etag.timestamp > latest.timestamp)) {
        return etag;
      }
      return latest;
    }, {
      timestamp: 0,
      lastUpdatedUserId: 0,
      lastUpdatedUserName: '',
    });

    yield put(boundaryItemsUpdateItems(
      baseBoundaryGroupId,
      updatedBoundaryStateItemsWithETags.map(({ boundaryStateItem }) => boundaryStateItem),
      latestEtag,
    ));
  }
  catch (error) {
    console.error('Error during boundary quick edit submission:', error);

    yield put(createAppError({
      type: AppErrorType.General,
      errorTitle: translate('An error occurred while updating the data'),
      title: translate('Boundary Update Error'),
    }));
  }
  finally {
    onDone();
  }
}
